import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Fields.css";
import * as turf from "@turf/turf";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast"; // Add this line

// --- 1. Define the conversion factors from Square Meters to other units ---
const CONVERSION_FACTORS = {
  acre: 4200.83,
  carat: 175.035,
  ha: 10000,
  m2: 1,
};

const mockFieldsData = [
  {
    id: 1,
    name: "Sector A-1",
    area: 15,
    areaUnit: "acre",
    notes: "Standard soil treatment applied.",
  },
  {
    id: 2,
    name: "Greenhouse B-3",
    area: 5,
    areaUnit: "acre",
    notes: "Hydroponic setup.",
  },
];

const FieldForm = () => {
  const { t } = useTranslation();
  const { fieldId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(fieldId);
  const { userData, token } = useContext(UserContext);

  const [formData, setFormData] = useState({
    name: "",
    area: "",
    areaUnit: "acre", // Default unit
    notes: "",
    boundaries: location.state?.boundaries || [],
  });

  const parseCustomLocationString = (locationString) => {
    if (!locationString || typeof locationString !== "string") {
      return [];
    }
    // 1. Split the string by the hyphen '-' to get individual coordinate pairs
    const pairs = locationString.split("-");

    // 2. Map over each pair
    return pairs.map((pair) => {
      // 3. Split each pair by the comma ',' and convert the parts to numbers
      const coords = pair.split(",").map(Number);
      return coords;
    });
  };

  const formatBoundariesToCustomString = (boundariesArray) => {
    if (!Array.isArray(boundariesArray)) return "";
    const coordStrings = boundariesArray.map((pair) => `${pair[0]},${pair[1]}`);
    return coordStrings.join("-");
  };

  // --- 2. New state to store the raw area in square meters ---
  const [areaInSquareMeters, setAreaInSquareMeters] = useState(0);
  const [loading, setLoading] = useState(isEditMode);

  // backend integration for data population
  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      // This logic assumes the component is only useful if it receives boundaries from the map editor.
      const newBoundaries = location.state?.boundaries;

      try {
        // We only proceed if new boundaries were passed from the map editor.
        if (newBoundaries && newBoundaries.length > 0) {
          // Calculate the new area based on the passed-in boundaries.
          const polygon = turf.polygon([newBoundaries]);
          const areaMeters = turf.area(polygon);
          setAreaInSquareMeters(areaMeters);

          const currentUnit = formData.areaUnit;
          const convertedArea = (
            areaMeters / CONVERSION_FACTORS[currentUnit]
          ).toFixed(2);

          let existingData = {};
          // If we are in edit mode, we still need to fetch the non-map data (name, notes, etc.)
          // to merge with our new boundaries.
          if (isEditMode && fieldId && token) {
            const response = await axios.get(`api/field/${fieldId}`, {
              headers: { Authorization: `${token}` },
            });
            if (response.data.items && response.data.items.length > 0) {
              existingData = response.data.items[0];
            }
          }

          // Set the form state, prioritizing the new boundaries and calculated area,
          // while merging existing data if in edit mode.
          setFormData({
            name: existingData.name || "",
            notes: existingData.notes || "",
            area: convertedArea,
            areaUnit: existingData.areaUnit || currentUnit,
            boundaries: newBoundaries,
          });
        }
      } catch (error) {
        console.error("Failed to load field data:", error);
        toast.error(t("Could not load field data. Please try again."));
        navigate("/fields");
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [fieldId, isEditMode, token, location.state, navigate]);

  // // mockList data population
  // useEffect(() => {
  //   if (isEditMode) {
  //     // MOCK FETCH: Find the field to edit from our mock data source
  //     const fieldToEdit = mockFieldsData.find(
  //       (f) => f.id === parseInt(fieldId)
  //     );
  //     if (fieldToEdit) {
  //       const areaMeters =
  //       fieldToEdit.area * CONVERSION_FACTORS[fieldToEdit.areaUnit];
  //       setAreaInSquareMeters(areaMeters);
  //       // Pre-fill the form with the existing data
  //       setFormData((prev) => ({ ...prev, ...fieldToEdit }));
  //     }
  //     setLoading(false);
  //   }

  //   // --- 4. Updated handler to recalculate area when the unit changes ---
  //   const boundaries = location.state?.boundaries;
  //   // --- 3. Auto-calculate area when the component loads with boundaries ---
  //   if (boundaries && boundaries.length > 3) {
  //     const polygon = turf.polygon([boundaries]);
  //     const areaMeters = turf.area(polygon);

  //     // Store the base area in square meters
  //     setAreaInSquareMeters(areaMeters);

  //     // Perform the initial calculation based on the default unit ('acre')
  //     const initialUnit = formData.areaUnit;
  //     const convertedArea = (
  //       areaMeters / CONVERSION_FACTORS[initialUnit]
  //     ).toFixed(2);

  //     // Update the form with the calculated area
  //     setFormData((prev) => ({ ...prev, area: convertedArea }));
  //   }
  // }, [fieldId, isEditMode, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "areaUnit") {
      // If the user is changing the unit, recalculate the area
      const newUnit = value;
      if (areaInSquareMeters > 0) {
        const recalculatedArea = (
          areaInSquareMeters / CONVERSION_FACTORS[newUnit]
        ).toFixed(2);
        setFormData((prev) => ({
          ...prev,
          area: recalculatedArea,
          areaUnit: newUnit,
        }));
      } else {
        // If there's no area yet, just update the unit
        setFormData((prev) => ({ ...prev, areaUnit: newUnit }));
      }
    } else {
      // Handle changes for all other inputs
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // mock submit
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   console.log("Saving field data:", formData);
  //   alert("Field saved successfully! (Mock)");
  //   navigate("/fields");
  // };

  // backend integration for submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const farmId = userData?.Farm?.id;
    const tenantId = userData?.Tenant?.id;

    // Guard clause: Ensure we have the necessary data before submitting
    if (!farmId || !tenantId || !token) {
      toast.error("User session is invalid. Please log in again.");
      return;
    }

    const apiPayload = {
      name: formData.name,
      notes: formData.notes,
      area: formData.area,
      areaUnit: formData.areaUnit,
      location: formatBoundariesToCustomString(formData.boundaries),
      farmId: farmId, // Use the value from context
      tenantId: tenantId, // Use the value from context
    };

    if (isEditMode) {
      apiPayload.id = parseInt(fieldId);
    }

    const apiUrl = `api/field`;
    const apiMethod = isEditMode ? "put" : "post";

    try {
      await axios({
        method: apiMethod,
        url: apiUrl,
        data: apiPayload,
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`, // Use token from context
        },
      });

      toast.success(t("Field saved successfully!"));
      if (isEditMode) {
        navigate(`/fields/${fieldId}`);
      } else {
        navigate("/fields");
      }
    } catch (error) {
      console.error("Error saving field:", error);
      toast.error(t("Failed to save the field."));
    }
  };

  if (loading)
    return <div className="page-message">{t("Loading Form...")}</div>;

  return (
    <div className="form-container">
      <div className="form-card">
        <h1>{isEditMode ? t("Edit Field") : t("Add New Field")}</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{t("Field Name")}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t("Enter field name")}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="area">{t("Area")}</label>
              <input
                type="number"
                id="area"
                name="area"
                value={formData.area}
                readOnly
                placeholder={t("Draw on map to calculate")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="areaUnit">{t("Area Unit")}</label>
              <select
                id="areaUnit"
                name="areaUnit"
                value={formData.areaUnit}
                onChange={handleChange}
              >
                <option value="acre">{t("acre")}</option>
                <option value="carat">{t("carat")}</option>
                <option value="ha">{t("ha")}</option>
                <option value="m2">{t("m²")}</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="notes">{t("Notes")}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder={t("Enter notes")}
            ></textarea>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate("/fields")}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className="button-primary">
              {isEditMode ? t("Save Changes") : t("Create Field")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FieldForm;
